import type { Ssm5DiagramModule, Ssm5TripTime } from '@packages/domain/ssm5';

import { colors } from '@packages/core/styles';
import { Typography } from '@packages/ui/shared';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import { formatSupplyVoltage, formatTimeCount, formatTripCount } from '../helpers/module-info';
import { NOT_COMMUNICATING_MESSAGE } from '../helpers/module-messages';
import { ssm5TextStyles } from '../ssm5.styles';
import { Ssm5ModuleMessage } from './ssm5-module-message';
import { Ssm5ReprogrammingNotice } from './ssm5-reprogramming-notice';

/** Props for {@link Ssm5ModuleInfoTab}. */
export type Ssm5ModuleInfoTabProps = {
    /** The module the panel shows. */
    module: Ssm5DiagramModule;
    /** The vehicle's supply voltage, when it reported a valid one. */
    supplyVoltage?: number;
    /** The vehicle's current ignition cycle and the time into it, when the read reported them. */
    tripTime?: Ssm5TripTime;
};

/** A row of the General Info tab: its label and its value, if there is one to show. */
type InfoRow = {
    /** The translation key of the label. */
    labelKey: string;
    /** The value, or undefined when the vehicle reported none. */
    value: string | undefined;
};

const useStyles = createUseStyles({
    // The rows take the panel's 16px inset; the notice sits at the bottom of the panel, as the design draws it.
    info: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        gap: 16,
        padding: '0 16px 16px 15px',
    },
    notice: {
        marginTop: 'auto',
    },
    // Each row is 56px high: its line between 16px of padding, closed by a divider.
    row: {
        borderBottom: `1px solid ${colors.grayThree}`,
        display: 'flex',
        gap: 16,
        justifyContent: 'space-between',
        padding: '16px 0',
    },
    rows: {
        display: 'flex',
        flexDirection: 'column',
    },
});

/**
 * The General Info tab of a module: its trip and time count, the vehicle's supply voltage, the module's
 * communication, calibration id and verification number, each on its own row, and the reprogramming
 * notice when the module can be reprogrammed. A value the vehicle did not report shows as not available.
 * A module that does not answer gets the not-communicating message in place of the rows.
 *
 * @param props - {@link Ssm5ModuleInfoTabProps}
 * @returns the tab body
 */
export function Ssm5ModuleInfoTab({ module, supplyVoltage, tripTime }: Ssm5ModuleInfoTabProps) {
    const classes = useStyles();
    const { t } = useTranslation();
    const rows: InfoRow[] = [
        { labelKey: 'ssm5:module.info.tripCount', value: formatTripCount(tripTime) },
        { labelKey: 'ssm5:module.info.timeCount', value: formatTimeCount(tripTime) },
        { labelKey: 'ssm5:module.info.supplyVoltage', value: formatSupplyVoltage(supplyVoltage) },
        { labelKey: 'ssm5:module.info.communication', value: t('ssm5:module.communication.active') },
        { labelKey: 'ssm5:module.info.cid', value: module.cid },
        { labelKey: 'ssm5:module.info.cvn', value: module.cvn },
    ];

    if (module.status === 'noConnection') {
        return (
            <div className={classes.info}>
                <Ssm5ModuleMessage message={NOT_COMMUNICATING_MESSAGE} />
            </div>
        );
    }

    return (
        <div className={classes.info}>
            <div className={classes.rows}>
                {rows.map((row) => (
                    <div className={classes.row} key={row.labelKey}>
                        <Typography style={ssm5TextStyles.moduleInfoText} variant="default">
                            {t(row.labelKey)}
                        </Typography>
                        <Typography style={ssm5TextStyles.moduleInfoText} variant="default">
                            {row.value ?? t('ssm5:module.info.unavailable')}
                        </Typography>
                    </div>
                ))}
            </div>
            {module.isReproAvailable && (
                <div className={classes.notice}>
                    <Ssm5ReprogrammingNotice reproUrl={module.reproUrl} />
                </div>
            )}
        </div>
    );
}
